import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ExternalLink, Check, Trash2, Loader2, X, Clock } from 'lucide-react';

/* ─── Platform Row (single platform in the list) ─── */
export const PlatformRow = ({ platform, existingLink, inputValue, isPending, isSaving, isEmbedable, onInputChange, onCheck, onConfirm, onCancel, onRemove, onToggleEmbed, onSchedule, onCategoryChange }) => {
  const Icon = platform.icon;
  const [showSchedule, setShowSchedule] = useState(false);
  const hasSchedule = existingLink?.scheduled_start || existingLink?.scheduled_end;

  return (
    <>
    <div
      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
        existingLink ? 'bg-green-500/5 border-green-500/20' : isPending ? 'bg-primary/5 border-primary/30 ring-1 ring-primary/20' : 'border-border hover:border-muted-foreground/30'
      }`}
      data-testid={`platform-row-${platform.id}`}
    >
      {/* Icon */}
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${platform.color}15` }}>
        <Icon className="w-5 h-5" style={{ color: platform.color }} />
      </div>

      {/* Name */}
      <span className="w-28 sm:w-36 font-medium text-sm truncate flex-shrink-0">{platform.name}</span>

      {/* Input / Filled State */}
      {existingLink ? (
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <a href={existingLink.url} target="_blank" rel="noopener noreferrer"
            className="text-sm text-muted-foreground hover:text-primary truncate flex items-center gap-1 flex-1 min-w-0">
            <span className="truncate">{existingLink.url}</span>
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>
          {isEmbedable && (
            <button
              onClick={() => onToggleEmbed(existingLink.id, existingLink.embed_enabled !== false)}
              className={`text-[10px] px-2 py-0.5 rounded-full border flex-shrink-0 transition-colors ${
                existingLink.embed_enabled !== false
                  ? 'bg-primary/10 border-primary/30 text-primary'
                  : 'bg-muted border-border text-muted-foreground'
              }`}
              title={existingLink.embed_enabled !== false ? 'Embed is ON — click to turn off' : 'Embed is OFF — click to turn on'}
              data-testid={`embed-toggle-${platform.id}`}
            >
              {existingLink.embed_enabled !== false ? 'Embed ON' : 'Embed OFF'}
            </button>
          )}
          <button onClick={() => setShowSchedule(!showSchedule)}
            className={`text-[10px] px-2 py-0.5 rounded-full border flex-shrink-0 transition-colors ${hasSchedule ? 'bg-orange-500/10 border-orange-500/30 text-orange-500' : 'bg-muted border-border text-muted-foreground hover:text-foreground'}`}
            title="Schedule this link" data-testid={`schedule-btn-${platform.id}`}>
            <Clock className="w-3 h-3 inline mr-0.5" />{hasSchedule ? 'Scheduled' : 'Schedule'}
          </button>
          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0 text-destructive/60 hover:text-destructive"
            onClick={() => onRemove(existingLink.id)} data-testid={`remove-platform-${platform.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ) : isPending ? (
        <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-center gap-2 min-w-0">
          <span className="text-sm font-medium truncate max-w-full">{inputValue}</span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-xs text-muted-foreground">Add this link?</span>
            <Button size="sm" className="h-7 px-3 text-xs" onClick={() => onConfirm(platform.id)} disabled={isSaving}
              data-testid={`confirm-platform-${platform.id}`}>
              {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" /> Yes</>}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onCancel(platform.id)}
              data-testid={`cancel-platform-${platform.id}`}>
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <Input
            value={inputValue}
            onChange={(e) => onInputChange(platform.id, e.target.value)}
            placeholder={platform.placeholder}
            className="h-9 text-sm flex-1 min-w-0"
            data-testid={`input-platform-${platform.id}`}
          />
          {inputValue && (
            <Button size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => onCheck(platform.id)}
              data-testid={`check-platform-${platform.id}`}>
              <Check className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}
    </div>
    {showSchedule && existingLink && (
      <div className="ml-12 p-3 rounded-lg bg-muted/30 border border-border space-y-2" data-testid={`schedule-panel-${platform.id}`}>
        <p className="text-xs font-medium flex items-center gap-1"><Clock className="w-3 h-3" /> Schedule this link</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <label className="text-[10px] text-muted-foreground">Goes live</label>
            <Input type="datetime-local" className="h-8 text-xs" defaultValue={existingLink.scheduled_start?.slice(0, 16) || ''}
              onChange={(e) => onSchedule(existingLink.id, e.target.value ? new Date(e.target.value).toISOString() : null, existingLink.scheduled_end)}
              data-testid={`schedule-start-${platform.id}`} />
          </div>
          <div className="flex-1">
            <label className="text-[10px] text-muted-foreground">Expires</label>
            <Input type="datetime-local" className="h-8 text-xs" defaultValue={existingLink.scheduled_end?.slice(0, 16) || ''}
              onChange={(e) => onSchedule(existingLink.id, existingLink.scheduled_start, e.target.value ? new Date(e.target.value).toISOString() : null)}
              data-testid={`schedule-end-${platform.id}`} />
          </div>
          <div className="flex-1">
            <label className="text-[10px] text-muted-foreground">Category</label>
            <Input className="h-8 text-xs" placeholder="e.g. Music, Social" defaultValue={existingLink.category || ''}
              onBlur={(e) => onCategoryChange(existingLink.id, e.target.value.trim())}
              data-testid={`category-${platform.id}`} />
          </div>
        </div>
        {hasSchedule && (
          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => { onSchedule(existingLink.id, null, null); setShowSchedule(false); }}>
            <X className="w-3 h-3 mr-1" /> Clear schedule
          </Button>
        )}
      </div>
    )}
    </>
  );
};
